import { compareArrays, compareHtmlText } from 'shared/util-common';

import {
  ComponentWidget,
  FlowchartNodeDto,
  FlowchartWidget,
  GuidelineComponent,
  GuidelineHeader,
  GuidelineMeta,
  GuidelineTag,
  GuidelineTagStatus,
  GuidelineWidget,
  TextWidget,
} from '@treetop/shared/model';

import { EditableGuidelineWidget } from '../guideline-graph.service';

export const mergeLocalChanges = <T extends EditableGuidelineWidget>(widget: T): T => {
  if (!widget) {
    return undefined;
  }

  let mergedWidget: T;

  if (widget.widgetType === 'text') {
    mergedWidget = {
      ...widget,
      text: widget.localChanges?.text ?? widget.text,
      references: widget.localChanges?.references ?? widget.references,
      tag: widget.localChanges?.tag ?? widget.tag,
    };
  } else if (widget.widgetType === 'flowchart') {
    mergedWidget = {
      ...widget,
      elements: widget.localChanges?.elements ?? widget.elements,
      tag: widget.localChanges?.tag ?? widget.tag,
    };
  } else if (widget.widgetType === 'chapter') {
    mergedWidget = {
      ...widget,
      attachments: widget.localChanges?.attachments ?? widget.attachments,
      resourceTags: widget.localChanges?.resourceTags ?? widget.resourceTags,
      tag: widget.localChanges?.tag ?? widget.tag,
    };
  } else if (widget.widgetType === 'component') {
    mergedWidget = {
      ...widget,
      attachments: widget.localChanges?.attachments ?? widget.attachments,
    };
  } else {
    mergedWidget = { ...widget };
  }

  delete mergedWidget.localChanges;

  return mergedWidget;
};

export const compareWidgets = (original: GuidelineWidget, current?: GuidelineWidget): boolean => {
  if (!current) {
    return true;
  }

  const sharedFieldsMatch =
    compareArrays(original.links ?? [], current.links ?? []) &&
    compareArrays(original.attachments ?? [], current.attachments ?? []) &&
    compareArrays(original.references ?? [], current.references ?? []);

  if (!sharedFieldsMatch || original.widgetType !== current.widgetType) {
    return true;
  }

  switch (original.widgetType) {
    case 'text':
      if (current.widgetType !== 'text') return true;
      return original.text !== current.text;

    case 'chapter':
      if (current.widgetType !== 'chapter') return true;
      return (
        original.title !== current.title ||
        !compareArrays(original.childrenIds ?? [], current.childrenIds ?? []) ||
        !compareArrays(original.widgetIds ?? [], current.widgetIds ?? []) ||
        !compareArrays(original.resourceTags ?? [], current.resourceTags ?? [], { ignoreOrder: true })
      );

    case 'component':
      if (current.widgetType !== 'component') return true;
      return (
        !compareArrays(original.childrenIds ?? [], current.childrenIds ?? []) ||
        !compareArrays(original.widgetIds ?? [], current.widgetIds ?? []) ||
        !compareArrays(original.resourceTags ?? [], current.resourceTags ?? [], { ignoreOrder: true }) ||
        JSON.stringify(original.component) !== JSON.stringify(current.component)
      );

    case 'component-list':
      if (current.widgetType !== 'component-list') return true;
      return (
        original.componentExpandStrategyOnRead !== current.componentExpandStrategyOnRead ||
        !compareArrays(original.widgetIds ?? [], current.widgetIds ?? []) ||
        original.componentListType !== current.componentListType
      );

    case 'flowchart': {
      if (current.widgetType !== 'flowchart') return true;

      return !compareArrays(current.elements, original.elements, {
        ignoreUndefinedMissingKeys: true,
        ignoreOrder: true,
        stringEqualFn: (a, b) => (a ?? '') === (b ?? ''),
      });
    }

    case 'workflow':
      if (current.widgetType !== 'workflow') return true;
      return current.workflowId !== original.workflowId || current.workflowVersion !== original.workflowVersion;

    default:
      return true;
  }
};

export const compareGuidelineMeta = (original: GuidelineMeta, current: GuidelineMeta): boolean => {
  if (!current) {
    return true;
  }

  if (original.updateMessage !== current.updateMessage) {
    return true;
  }

  return !compareArrays(
    original.taxonomies.map((taxonomy) => taxonomy.id),
    current.taxonomies.map((taxonomy) => taxonomy.id),
  );
};

export const compareGuidelineHeaders = (original: GuidelineHeader, current?: GuidelineHeader): boolean => {
  if (!current) {
    return true;
  }

  return (
    original.title !== current.title ||
    original.code !== current.code ||
    original.ownerId !== current.ownerId ||
    original.allEditorsCanEdit !== current.allEditorsCanEdit ||
    original.additionalInfo !== current.additionalInfo ||
    original.languageCode !== current.languageCode ||
    new Date(original.revisionDate).getTime() !== new Date(current.revisionDate).getTime() ||
    JSON.stringify(original.revisionDateTimeSpan) !== JSON.stringify(current.revisionDateTimeSpan) ||
    !compareArrays(original.authors ?? [], current.authors ?? [], {
      ignoreOrder: true,
    }) ||
    !compareArrays(original.codings ?? [], current.codings ?? [], {
      ignoreOrder: true,
    })
  );
};

export const compareLocalWidgetValues = (a: GuidelineWidget, b?: GuidelineWidget): boolean => {
  if (!a || !b) return false;

  if (a.widgetType !== b.widgetType) return false;

  switch (a.widgetType) {
    case 'chapter': {
      return (
        compareArrays(a.attachments ?? [], (b as GuidelineWidget).attachments ?? [], {
          ignoreUndefinedMissingKeys: true,
          ignoreOrder: true,
          stringEqualFn: (a, b) => (a ?? '') === (b ?? ''),
        }) &&
        compareArrays(a.resourceTags ?? [], (b as GuidelineWidget).resourceTags ?? [], {
          ignoreOrder: true,
        })
      );
    }

    case 'component': {
      return (
        compareArrays(a.attachments ?? [], (b as ComponentWidget).attachments ?? [], {
          ignoreUndefinedMissingKeys: true,
          ignoreOrder: true,
          stringEqualFn: (a, b) => (a ?? '') === (b ?? ''),
        }) &&
        compareArrays(a.resourceTags ?? [], (b as ComponentWidget).resourceTags ?? [], {
          ignoreOrder: true,
        })
      );
    }

    case 'text': {
      return compareHtmlText(a.text, (b as TextWidget).text);
    }

    case 'flowchart': {
      return compareArrays(a.elements ?? [], (b as FlowchartWidget).elements ?? [], {
        ignoreUndefinedMissingKeys: true,
        ignoreOrder: true,
        stringEqualFn: (a, b) => (a ?? '') === (b ?? ''),
      });
    }

    default:
      return false;
  }
};

export const getComponentWidgetTag = (
  current: { component?: GuidelineComponent; updatedWidgetIds?: string[]; tag?: GuidelineTag },
  original: { component?: GuidelineComponent; updatedWidgetIds?: string[]; tag?: GuidelineTag },
): GuidelineTag | undefined => {
  if (JSON.stringify(original.component) !== JSON.stringify(current.component)) {
    return {
      ...current.tag,
      status: GuidelineTagStatus.UPDATED,
    };
  }

  if (current.updatedWidgetIds?.length > 0) {
    return {
      ...current.tag,
      status: GuidelineTagStatus.CHILD_UPDATED,
    };
  }

  return undefined;
};

export const getChapterWidgetTag = (
  current: {
    title?: string;
    updatedWidgetIds?: string[];
    tag?: GuidelineTag;
  },
  original?: {
    title?: string;
    updatedWidgetIds?: string[];
    tag?: GuidelineTag;
  },
): GuidelineTag | undefined => {
  if (current.title !== original?.title) {
    return {
      ...current.tag,
      status: GuidelineTagStatus.UPDATED,
    };
  }

  if (current.updatedWidgetIds?.length > 0) {
    return {
      ...current.tag,
      status: GuidelineTagStatus.CHILD_UPDATED,
    };
  }

  return undefined;
};

export const getTextWidgetTag = (
  current: {
    text?: string;
    tag?: GuidelineTag;
  },
  original?: {
    text?: string;
    tag?: GuidelineTag;
  },
): GuidelineTag | undefined => {
  const currentText = current.text;
  const originalText = original?.text;

  if (currentText === undefined || currentText === null) {
    return undefined;
  }

  if (!compareHtmlText(currentText, originalText)) {
    return {
      ...current.tag,
      status: GuidelineTagStatus.UPDATED,
    };
  }

  return undefined;
};

export const getFlowchartWidgetTag = (
  current: {
    elements?: FlowchartNodeDto[];
    tag?: GuidelineTag;
  },
  original?: {
    elements?: FlowchartNodeDto[];
    tag?: GuidelineTag;
  },
): GuidelineTag | undefined => {
  if (!current?.elements) {
    return undefined;
  }

  const hasFlowchartChanged = !compareArrays(current.elements, original?.elements, {
    ignoreUndefinedMissingKeys: true,
    ignoreOrder: true,
    stringEqualFn: (a, b) => (a ?? '') === (b ?? ''),
  });

  if (hasFlowchartChanged) {
    return {
      ...current.tag,
      status: GuidelineTagStatus.UPDATED,
    };
  }

  return undefined;
};

export const getWorkflowWidgetTag = (
  current: {
    workflowId?: string;
    workflowVersion?: number;
    tag?: GuidelineTag;
  },
  original?: {
    workflowId?: string;
    workflowVersion?: number;
    tag?: GuidelineTag;
  },
): GuidelineTag | undefined => {
  if (current.workflowId !== original?.workflowId || current.workflowVersion !== original?.workflowVersion) {
    return {
      ...current.tag,
      status: GuidelineTagStatus.UPDATED,
    };
  }

  return undefined;
};

export const getGuidelineElementTag = (
  element: GuidelineWidget,
  published?: GuidelineWidget,
  isImportedGuideline = false,
): GuidelineTag | undefined => {
  if (!published) {
    if (element.tag) {
      return element.tag;
    }

    return {
      isActive: true,
      status: GuidelineTagStatus.NEW,
    };
  }

  if (element.tag?.status === GuidelineTagStatus.NEW) {
    return element.tag;
  }

  if (
    !compareArrays(element.attachments ?? [], published.attachments ?? [], {
      ignoreUndefinedMissingKeys: true,
      ignoreOrder: true,
      stringEqualFn: (a, b) => (a ?? '') === (b ?? ''),
    })
  ) {
    return {
      ...element.tag,
      status: GuidelineTagStatus.UPDATED,
    };
  }

  if (
    !compareArrays(element.links ?? [], published.links ?? [], {
      ignoreUndefinedMissingKeys: true,
      ignoreOrder: true,
      stringEqualFn: (a, b) => (a ?? '') === (b ?? ''),
    })
  ) {
    return {
      ...element.tag,
      status: GuidelineTagStatus.UPDATED,
    };
  }

  if (
    !compareArrays(element.references ?? [], published.references ?? [], {
      ignoreUndefinedMissingKeys: true,
      ignoreOrder: true,
      stringEqualFn: (a, b) => (a ?? '') === (b ?? ''),
    })
  ) {
    return {
      ...element.tag,
      status: GuidelineTagStatus.UPDATED,
    };
  }

  if (element.widgetType === 'chapter' && published.widgetType === 'chapter') {
    return getChapterWidgetTag(element, isImportedGuideline ? element.localChanges : published);
  }

  if (element.widgetType === 'component' && published.widgetType === 'component') {
    return getComponentWidgetTag(element, published);
  }

  if (element.widgetType === 'text' && published.widgetType === 'text') {
    return getTextWidgetTag(element, isImportedGuideline ? element.localChanges : published);
  }

  if (element.widgetType === 'flowchart' && published.widgetType === 'flowchart') {
    return getFlowchartWidgetTag(element, isImportedGuideline ? element.localChanges : published);
  }

  if (element.widgetType === 'workflow' && published.widgetType === 'workflow') {
    return getWorkflowWidgetTag(element, isImportedGuideline ? element.localChanges : published);
  }

  return undefined;
};
